import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  PERMISSIONS,
  isPracticalExamWizardStep,
  type PracticalExamSheetStatus,
  type PracticalExamWizardStep,
} from '@lms/shared-types';
import { ConfirmModal, Loader, useToast } from '@lms/ui';
import { HubBackLink } from '@/components/hub/HubBackLink';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { usePermissions } from '@/hooks/usePermissions';
import { api } from '@/lib/api';
import { roleActionErrorToast } from '@/lib/user-feedback';

interface PracticalExamListItem {
  id: string;
  applicantName: string;
  run: string;
  examDate: string;
  examinerName: string;
  plate: string;
  mildCount: number;
  seriousCount: number;
  failingCount: number;
  passed: boolean;
  status: PracticalExamSheetStatus;
  wizardStep?: PracticalExamWizardStep;
}

interface ListResponse {
  data: PracticalExamListItem[];
  meta: { total: number; page: number; perPage: number; lastPage: number };
}

type ResultFilter = '' | 'approved' | 'failed';

const RESULT_FILTERS: { value: ResultFilter; label: string }[] = [
  { value: '', label: 'Todos los resultados' },
  { value: 'approved', label: 'Aprobados' },
  { value: 'failed', label: 'Reprobados' },
];

const WIZARD_STEP_LABEL: Record<PracticalExamWizardStep, string> = {
  student: 'elegir alumno',
  vehicle: 'el vehículo',
  km: 'el kilometraje inicial',
  sheet: 'la pauta',
  'km-end': 'el kilometraje final',
  'student-sign': 'la firma del alumno',
  'examiner-sign': 'la firma del instructor',
};

const FILTER_FIELD =
  'w-full rounded-lg border border-dash-outline-variant bg-white py-3 px-4 text-base outline-none transition-all focus:border-dash-secondary focus:ring-2 focus:ring-dash-secondary/20';

function wizardStepLabel(step: PracticalExamWizardStep | undefined): string {
  return WIZARD_STEP_LABEL[isPracticalExamWizardStep(step) ? step : 'student'];
}

function formatDate(value: string): string {
  const [year, month, day] = value.split('-');
  if (!year || !month || !day) return value;
  return `${day}-${month}-${year}`;
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  const first = parts[0]?.[0] ?? '';
  const second = parts[1]?.[0] ?? parts[0]?.[1] ?? '';
  return (first + second).toUpperCase();
}

function StatusBadge({ status }: { status: PracticalExamSheetStatus }) {
  if (status === 'final') {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700">
        <span className="h-1.5 w-1.5 rounded-full bg-slate-500" aria-hidden="true" />
        Finalizada
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-800">
      <span className="h-1.5 w-1.5 rounded-full bg-amber-500" aria-hidden="true" />
      Borrador
    </span>
  );
}

function ResultBadge({ passed }: { passed: boolean }) {
  if (passed) {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-700">
        <span className="h-1.5 w-1.5 rounded-full bg-emerald-600" aria-hidden="true" />
        Aprobado
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-rose-50 px-2.5 py-1 text-xs font-medium text-rose-700">
      <span className="h-1.5 w-1.5 rounded-full bg-rose-600" aria-hidden="true" />
      Reprobado
    </span>
  );
}

function PlateCell({ plate }: { plate: string }) {
  const trimmed = plate.trim();
  if (!trimmed) {
    return <span className="font-medium text-slate-400">—</span>;
  }
  return (
    <span className="rounded bg-slate-100 px-2 py-0.5 text-xs font-medium uppercase tracking-wide text-slate-700">
      {trimmed}
    </span>
  );
}

export function PracticalExamListPage() {
  const { hasPermission } = usePermissions();
  const canCreate = hasPermission(PERMISSIONS.EVALUACIONES_CREAR);
  const canEdit = hasPermission(PERMISSIONS.EVALUACIONES_EDITAR);
  const canDelete = hasPermission(PERMISSIONS.EVALUACIONES_ELIMINAR);
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [searchQuery, setSearchQuery] = useState('');
  const [result, setResult] = useState<ResultFilter>('');
  const [page, setPage] = useState(1);
  const [pendingDelete, setPendingDelete] = useState<PracticalExamListItem | null>(null);

  const searchTerm = useDebouncedValue(searchQuery, 300).trim();

  const { data, isLoading, error } = useQuery({
    queryKey: ['practical-exams', searchTerm, result, page],
    queryFn: () => {
      const params = new URLSearchParams();
      if (searchTerm) params.set('search', searchTerm);
      if (result) params.set('result', result);
      params.set('page', String(page));
      return api<ListResponse>(`/practical-exams?${params.toString()}`);
    },
  });

  useEffect(() => {
    setPage(1);
  }, [searchTerm, result]);

  const rows = data?.data ?? [];
  const meta = data?.meta;
  const total = meta?.total ?? 0;
  const perPage = meta?.perPage ?? (rows.length || 1);
  const lastPage = meta?.lastPage ?? 1;
  const showingFrom = total === 0 ? 0 : (page - 1) * perPage + 1;
  const showingTo = total === 0 ? 0 : Math.min(page * perPage, total);

  const remove = useMutation({
    mutationFn: (id: string) => api(`/practical-exams/${id}`, { method: 'DELETE' }),
    onSuccess: async () => {
      const removed = pendingDelete;
      setPendingDelete(null);
      if (rows.length === 1 && page > 1) setPage((current) => current - 1);
      await queryClient.invalidateQueries({ queryKey: ['practical-exams'] });
      toast({
        title: 'Ficha eliminada',
        description: removed ? `Se eliminó la ficha de ${removed.applicantName}.` : undefined,
        variant: 'success',
      });
    },
    onError: (removeError) => toast(roleActionErrorToast(removeError, 'No se pudo eliminar la ficha')),
  });

  const actionBtn =
    'inline-flex h-8 w-8 items-center justify-center rounded-lg text-slate-500 transition-colors';

  return (
    <div className="lms-site-admin-hub-page font-sans">
      <div className="mb-6 flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
        <div className="flex items-start gap-4">
          <HubBackLink to="/administracion?tab=plugins" label="Volver a plugins" />
          <div className="min-w-0 flex-1 flex flex-col">
            <h1 className="text-3xl font-semibold text-dash-primary md:text-4xl">Examen práctico</h1>
            <p className="mt-1 max-w-2xl text-dash-on-surface-variant">
              La misma pauta de la ficha: 15 bloques, faltas leves, graves y reprobatorias. El resultado se calcula al guardar.
            </p>
          </div>
        </div>
        {canCreate ? (
          <div className="flex shrink-0 items-center gap-3 self-start sm:self-center">
            <Link
              to="/examen-practico/nuevo"
              className="inline-flex items-center gap-2 rounded-lg bg-dash-primary px-5 py-2.5 text-sm font-medium text-white shadow-xs transition-opacity hover:opacity-90"
            >
              <span className="material-symbols-outlined text-[18px]" aria-hidden="true">add</span>
              <span>Nueva ficha</span>
            </Link>
          </div>
        ) : null}
      </div>

      {error ? (
        <p className="mb-4 text-sm text-error-600" role="alert">
          {(error as Error).message}
        </p>
      ) : null}

      <div className="flex flex-col overflow-hidden rounded-xl border border-dash-outline-variant bg-white shadow-xs">
        <div className="flex flex-col items-stretch justify-between gap-3 border-b border-slate-100 p-5 sm:flex-row sm:items-center">
          <div className="flex w-full items-center gap-1.5 overflow-x-auto sm:w-auto">
            {RESULT_FILTERS.map((filter) => {
              const active = result === filter.value;
              return (
                <button
                  key={filter.value || 'all'}
                  type="button"
                  onClick={() => setResult(filter.value)}
                  className={
                    active
                      ? 'shrink-0 rounded-lg bg-dash-primary px-3.5 py-1.5 text-xs font-semibold text-white transition-colors'
                      : 'shrink-0 rounded-lg px-3.5 py-1.5 text-xs font-medium text-slate-600 transition-colors hover:bg-slate-100 hover:text-slate-900'
                  }
                >
                  {filter.label}
                </button>
              );
            })}
          </div>
          <form
            className="flex w-full items-center gap-2 sm:w-auto"
            onSubmit={(event) => {
              event.preventDefault();
              setPage(1);
            }}
          >
            <div className="relative w-full min-w-0 sm:w-80">
              <span
                className="material-symbols-outlined pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-dash-on-surface-variant"
                aria-hidden="true"
              >
                search
              </span>
              <input
                type="search"
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                placeholder="Postulante, R.U.N., patente o examinador..."
                aria-label="Buscar ficha de examen práctico"
                className={`${FILTER_FIELD} pl-12`}
              />
            </div>
          </form>
        </div>

        {isLoading ? (
          <div className="flex justify-center py-16">
            <Loader />
          </div>
        ) : rows.length === 0 ? (
          <p className="px-6 py-16 text-center text-dash-on-surface-variant">
            {searchTerm || result
              ? 'No se encontraron fichas con ese criterio.'
              : 'No hay fichas registradas.'}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[980px] border-collapse text-left">
              <thead>
                <tr className="border-b border-dash-outline-variant bg-[#f8f9fc] text-xs font-semibold uppercase tracking-wider text-slate-500">
                  <th className="whitespace-nowrap px-6 py-3.5" scope="col">Fecha</th>
                  <th className="whitespace-nowrap px-6 py-3.5" scope="col">Postulante</th>
                  <th className="whitespace-nowrap px-6 py-3.5" scope="col">R.U.N.</th>
                  <th className="whitespace-nowrap px-6 py-3.5" scope="col">Patente</th>
                  <th className="whitespace-nowrap px-4 py-3.5 text-center" scope="col">L</th>
                  <th className="whitespace-nowrap px-4 py-3.5 text-center" scope="col">G</th>
                  <th className="whitespace-nowrap px-4 py-3.5 text-center" scope="col">R</th>
                  <th className="whitespace-nowrap px-6 py-3.5 text-center" scope="col">Estado</th>
                  <th className="whitespace-nowrap px-6 py-3.5 text-center" scope="col">Resultado</th>
                  <th className="whitespace-nowrap px-6 py-3.5 text-right" scope="col">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-sm">
                {rows.map((row) => (
                  <tr key={row.id} className="transition-colors hover:bg-slate-50/60">
                    <td className="whitespace-nowrap px-6 py-4 text-slate-700">{formatDate(row.examDate)}</td>
                    <td className="whitespace-nowrap px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div
                          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-slate-100 text-xs font-semibold text-slate-700"
                          aria-hidden="true"
                        >
                          {initials(row.applicantName || 'SP')}
                        </div>
                        <div className="flex flex-col">
                          <span className="font-semibold text-slate-900">{row.applicantName.trim() || 'Sin postulante'}</span>
                          <span className="text-xs text-slate-400">{row.examinerName}</span>
                        </div>
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-slate-600">{row.run}</td>
                    <td className="whitespace-nowrap px-6 py-4">
                      <PlateCell plate={row.plate} />
                    </td>
                    <td className="whitespace-nowrap px-4 py-4 text-center font-medium text-slate-700">{row.mildCount}</td>
                    <td className="whitespace-nowrap px-4 py-4 text-center font-medium text-slate-700">{row.seriousCount}</td>
                    <td className="whitespace-nowrap px-4 py-4 text-center font-medium text-slate-700">{row.failingCount}</td>
                    <td className="whitespace-nowrap px-6 py-4 text-center">
                      <StatusBadge status={row.status === 'final' ? 'final' : 'draft'} />
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-center">
                      {row.status === 'final' ? (
                        <ResultBadge passed={row.passed} />
                      ) : (
                        <span className="text-xs font-medium text-slate-400">En curso</span>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-right">
                      <div className="inline-flex items-center justify-end gap-1">
                        {canEdit && row.status !== 'final' ? (
                          <Link
                            to={`/examen-practico/${row.id}?modo=reanudar`}
                            className={`${actionBtn} hover:bg-slate-100 hover:text-dash-primary`}
                            title={`Reanudar en ${wizardStepLabel(row.wizardStep)}`}
                            aria-label={`Reanudar ficha de ${row.applicantName || 'sin postulante'} en ${wizardStepLabel(row.wizardStep)}`}
                          >
                            <span className="material-symbols-outlined text-[20px]" aria-hidden="true">play_arrow</span>
                          </Link>
                        ) : null}
                        <Link
                          to={`/examen-practico/${row.id}`}
                          className={`${actionBtn} hover:bg-slate-100 hover:text-dash-primary`}
                          title="Ver ficha"
                          aria-label={`Ver ficha de ${row.applicantName || 'sin postulante'}`}
                        >
                          <span className="material-symbols-outlined text-[20px]" aria-hidden="true">visibility</span>
                        </Link>
                        {canEdit && row.status !== 'final' ? (
                          <Link
                            to={`/examen-practico/${row.id}?modo=editar`}
                            className={`${actionBtn} hover:bg-slate-100 hover:text-dash-primary`}
                            title="Editar"
                            aria-label={`Editar ficha de ${row.applicantName}`}
                          >
                            <span className="material-symbols-outlined text-[20px]" aria-hidden="true">edit</span>
                          </Link>
                        ) : null}
                        {canDelete ? (
                          <button
                            type="button"
                            onClick={() => setPendingDelete(row)}
                            className={`${actionBtn} hover:bg-rose-50 hover:text-rose-600`}
                            title="Eliminar"
                            aria-label={`Eliminar ficha de ${row.applicantName}`}
                          >
                            <span className="material-symbols-outlined text-[20px]" aria-hidden="true">delete</span>
                          </button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!isLoading && total > 0 ? (
          <div className="flex flex-col items-center justify-between gap-3 border-t border-slate-100 bg-slate-50/60 px-6 py-3.5 sm:flex-row">
            <span className="text-xs text-slate-500">
              Mostrando{' '}
              <span className="font-semibold text-slate-700">{showingFrom}</span>
              {' a '}
              <span className="font-semibold text-slate-700">{showingTo}</span>
              {' de '}
              <span className="font-semibold text-slate-700">{total}</span>
              {' resultados'}
            </span>
            <div className="flex items-center gap-1.5">
              <button
                type="button"
                aria-label="Página anterior"
                disabled={page <= 1}
                onClick={() => setPage((current) => current - 1)}
                className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 transition-colors hover:bg-slate-200/50 disabled:cursor-not-allowed disabled:opacity-40"
              >
                <span className="material-symbols-outlined text-[18px]" aria-hidden="true">chevron_left</span>
              </button>
              <div
                className="flex h-8 w-8 items-center justify-center rounded-lg bg-dash-primary text-xs font-semibold text-white shadow-xs"
                aria-current="page"
              >
                {page}
              </div>
              <button
                type="button"
                aria-label="Página siguiente"
                disabled={page >= lastPage}
                onClick={() => setPage((current) => current + 1)}
                className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 transition-colors hover:bg-slate-200/50 disabled:cursor-not-allowed disabled:opacity-40"
              >
                <span className="material-symbols-outlined text-[18px]" aria-hidden="true">chevron_right</span>
              </button>
            </div>
          </div>
        ) : null}
      </div>

      <ConfirmModal
        open={Boolean(pendingDelete)}
        title="Eliminar ficha"
        description={
          pendingDelete
            ? `Se borra la ficha de ${pendingDelete.applicantName}. Esta acción no se puede deshacer.`
            : ''
        }
        confirmLabel="Eliminar"
        loading={remove.isPending}
        onClose={() => setPendingDelete(null)}
        onConfirm={() => pendingDelete && remove.mutate(pendingDelete.id)}
      />
    </div>
  );
}
